import React, { useState, useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Calendar as CalendarIcon,
  ChevronLeft,
  ChevronRight,
  Plus,
  Clock,
  AlertTriangle,
  Scale,
  Video,
  ExternalLink,
  Folder,
  CheckCircle2,
  Share2,
  Download,
  Filter,
  Search,
  Check,
  Edit2,
  Trash2,
  Calculator,
  Gavel,
  FileText,
  User,
  Building,
  Sparkles,
  Info,
  CalendarCheck2,
  Tag
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from "@/components/ui/dialog";
import { ScrollArea } from "@/components/ui/scroll-area";
import { useDespachos, Deadline, Process } from "@/hooks/useDespachos";
import { useToast } from "@/components/ui/use-toast";
import { toast as sonnerToast } from "sonner";
import { cn } from "@/lib/utils";

// Festivos Oficiales de Colombia 2024 - 2026 (Ley Emiliani + Ley 270)
const COLOMBIAN_HOLIDAYS: Record<string, string> = {
  // 2024
  "2024-01-01": "Año Nuevo",
  "2024-01-08": "Reyes Magos",
  "2024-03-25": "San José",
  "2024-03-28": "Jueves Santo",
  "2024-03-29": "Viernes Santo",
  "2024-05-01": "Día del Trabajo",
  "2024-05-13": "Ascensión del Señor",
  "2024-06-03": "Corpus Christi",
  "2024-06-10": "Sagrado Corazón",
  "2024-07-01": "San Pedro y San Pablo",
  "2024-07-20": "Día de la Independencia",
  "2024-08-07": "Batalla de Boyacá",
  "2024-08-19": "Asunción de la Virgen",
  "2024-10-14": "Día de la Raza",
  "2024-11-04": "Todos los Santos",
  "2024-11-11": "Independencia de Cartagena",
  "2024-12-08": "Inmaculada Concepción",
  "2024-12-25": "Navidad",
  // 2025
  "2025-01-01": "Año Nuevo",
  "2025-01-06": "Reyes Magos",
  "2025-03-24": "San José",
  "2025-04-17": "Jueves Santo",
  "2025-04-18": "Viernes Santo",
  "2025-05-01": "Día del Trabajo",
  "2025-06-02": "Ascensión del Señor",
  "2025-06-23": "Corpus Christi",
  "2025-06-30": "San Pedro y San Pablo",
  "2025-07-20": "Día de la Independencia",
  "2025-08-07": "Batalla de Boyacá",
  "2025-08-18": "Asunción de la Virgen",
  "2025-10-13": "Día de la Raza",
  "2025-11-03": "Todos los Santos",
  "2025-11-17": "Independencia de Cartagena",
  "2025-12-08": "Inmaculada Concepción",
  "2025-12-25": "Navidad",
  // 2026
  "2026-01-01": "Año Nuevo",
  "2026-01-12": "Reyes Magos",
  "2026-03-23": "San José",
  "2026-04-02": "Jueves Santo",
  "2026-04-03": "Viernes Santo",
  "2026-05-01": "Día del Trabajo",
  "2026-05-18": "Ascensión del Señor",
  "2026-06-08": "Corpus Christi",
  "2026-06-15": "Sagrado Corazón",
  "2026-06-29": "San Pedro y San Pablo",
  "2026-07-20": "Día de la Independencia",
  "2026-08-07": "Batalla de Boyacá",
  "2026-08-17": "Asunción de la Virgen",
  "2026-10-12": "Día de la Raza",
  "2026-11-02": "Todos los Santos",
  "2026-11-16": "Independencia de Cartagena",
  "2026-12-08": "Inmaculada Concepción",
  "2026-12-25": "Navidad"
};

// Determina si una fecha es día no hábil en la Rama Judicial (Sábados, Domingos, Festivos o Vacancia Judicial)
const isNonWorkingDay = (d: Date): { isNonWorking: boolean; reason?: string } => {
  const dayOfWeek = d.getDay();
  if (dayOfWeek === 0) return { isNonWorking: true, reason: "Domingo" };
  if (dayOfWeek === 6) return { isNonWorking: true, reason: "Sábado" };

  const iso = d.toISOString().split("T")[0];
  if (COLOMBIAN_HOLIDAYS[iso]) {
    return { isNonWorking: true, reason: COLOMBIAN_HOLIDAYS[iso] };
  }

  // Vacancia Judicial anual: del 20 de diciembre al 10 de enero (Art. 146 Ley 270 de 1996)
  const month = d.getMonth() + 1; // 1-12
  const day = d.getDate();
  if ((month === 12 && day >= 20) || (month === 1 && day <= 10)) {
    return { isNonWorking: true, reason: "Vacancia Judicial (Ley 270)" };
  }

  return { isNonWorking: false };
};

// Calcula el vencimiento de un término en días hábiles procesales conforme al Art. 118 CGP
export const calculateBusinessTermDate = (startDateStr: string, termDays: number): Date => {
  const start = new Date(startDateStr + "T00:00:00");
  let current = new Date(start);
  let daysCounted = 0;

  // En Colombia el término corre a partir del día hábil siguiente a la notificación (Art. 118 CGP)
  while (daysCounted < termDays) {
    current.setDate(current.getDate() + 1);
    const { isNonWorking } = isNonWorkingDay(current);
    if (!isNonWorking) {
      daysCounted++;
    }
  }

  return current;
};

export type EventType = "audiencia" | "termino" | "estado" | "diligencia" | "otro";

interface StructuredEventMetadata {
  type: EventType;
  time?: string;
  meetingUrl?: string;
  priority?: "alta" | "media" | "baja";
  juzgado?: string;
  radicado?: string;
  clientName?: string;
  rawNotes?: string;
}

// Parsea descripción con posibles metadatos estructurados
const parseEventDescription = (rawDesc?: string | null): StructuredEventMetadata => {
  if (!rawDesc) {
    return { type: "termino", priority: "media" };
  }

  try {
    if (rawDesc.startsWith("{") && rawDesc.endsWith("}")) {
      const parsed = JSON.parse(rawDesc);
      return {
        type: parsed.type || "termino",
        time: parsed.time,
        meetingUrl: parsed.meetingUrl,
        priority: parsed.priority || "media",
        juzgado: parsed.juzgado,
        radicado: parsed.radicado,
        clientName: parsed.clientName,
        rawNotes: parsed.rawNotes || ""
      };
    }
  } catch (e) {
    // No es JSON, intentar detectar por palabras clave
  }

  const lower = rawDesc.toLowerCase();
  let detectedType: EventType = "termino";
  if (lower.includes("audiencia") || lower.includes("teams") || lower.includes("zoom")) {
    detectedType = "audiencia";
  } else if (lower.includes("estado") || lower.includes("traslado") || lower.includes("fijación")) {
    detectedType = "estado";
  } else if (lower.includes("inspección") || lower.includes("diligencia") || lower.includes("secuestro")) {
    detectedType = "diligencia";
  }

  return {
    type: detectedType,
    priority: lower.includes("urgente") || lower.includes("preclusión") ? "alta" : "media",
    rawNotes: rawDesc
  };
};

export default function CalendarioProcesalDespachos() {
  const navigate = useNavigate();
  const { deadlines, processes, addEvent, updateEvent, deleteEvent, loading } = useDespachos();
  const { toast } = useToast();

  // Vista actual y fecha seleccionada
  const [currentDate, setCurrentDate] = useState<Date>(new Date());
  const [viewMode, setViewMode] = useState<"mes" | "semana" | "vencimientos">("mes");
  const [selectedProcessFilter, setSelectedProcessFilter] = useState<string>("todos");
  const [selectedTypeFilter, setSelectedTypeFilter] = useState<string>("todos");
  const [selectedTagFilter, setSelectedTagFilter] = useState<string>("todos");
  const [searchQuery, setSearchQuery] = useState<string>("");

  // Modales
  const [isDialogOpen, setIsDialogOpen] = useState<boolean>(false);
  const [isCalcOpen, setIsCalcOpen] = useState<boolean>(false);
  const [editingDeadline, setEditingDeadline] = useState<Deadline | null>(null);

  // Formulario del evento
  const [eventForm, setEventForm] = useState({
    title: "",
    dueDate: new Date().toISOString().split("T")[0],
    caseId: "",
    type: "termino" as EventType,
    time: "09:00",
    meetingUrl: "",
    priority: "alta" as "alta" | "media" | "baja",
    notes: ""
  });

  // Formulario de la calculadora de términos
  const [calcForm, setCalcForm] = useState({
    notifyDate: new Date().toISOString().split("T")[0],
    termDays: 5,
    customDays: 5
  });

  // Etiquetas únicas disponibles para filtrar
  const availableTags = useMemo(() => {
    const set = new Set<string>();
    processes.forEach((p) => {
      (p.tags || []).forEach((t) => {
        if (t && typeof t === "string" && t.trim()) set.add(t.trim());
      });
    });
    return Array.from(set);
  }, [processes]);

  // Mapeo enriquecido de los eventos con su proceso asociado
  const enrichedEvents = useMemo(() => {
    return deadlines.map((dl) => {
      const meta = parseEventDescription(dl.description);
      const associatedProcess = processes.find((p) => p.id === dl.case_id);

      const radicado = associatedProcess?.case_number || associatedProcess?.radicado || meta.radicado || "";
      const juzgado = associatedProcess?.juzgado || associatedProcess?.despacho || meta.juzgado || "";
      const clientName = associatedProcess?.client_name || meta.clientName || "";
      const processTitle = associatedProcess?.title || "";
      const tags: string[] = associatedProcess?.tags || [];

      // Cálculo de días restantes
      const due = new Date(dl.due_date + "T00:00:00");
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const diffMs = due.getTime() - today.getTime();
      const diffDays = Math.ceil(diffMs / (1000 * 60 * 60 * 24));

      return {
        ...dl,
        meta,
        associatedProcess,
        radicado,
        juzgado,
        clientName,
        processTitle,
        tags,
        diffDays,
        isOverdue: diffDays < 0 && dl.status !== "completed",
        isToday: diffDays === 0,
        isUrgent: diffDays >= 0 && diffDays <= 3 && dl.status !== "completed"
      };
    });
  }, [deadlines, processes]);

  // Eventos filtrados
  const filteredEvents = useMemo(() => {
    return enrichedEvents.filter((ev) => {
      if (selectedProcessFilter !== "todos" && ev.case_id !== selectedProcessFilter) {
        return false;
      }
      if (selectedTypeFilter !== "todos" && ev.meta.type !== selectedTypeFilter) {
        return false;
      }
      if (selectedTagFilter !== "todos") {
        const evTags = (ev.tags || []).map((t) => t.toLowerCase());
        if (!evTags.includes(selectedTagFilter.toLowerCase())) {
          return false;
        }
      }
      if (searchQuery.trim()) {
        const q = searchQuery.toLowerCase();
        const matchesTitle = ev.title.toLowerCase().includes(q);
        const matchesRad = ev.radicado.toLowerCase().includes(q);
        const matchesJuzgado = ev.juzgado.toLowerCase().includes(q);
        const matchesProc = ev.processTitle.toLowerCase().includes(q);
        const matchesClient = ev.clientName.toLowerCase().includes(q);
        const matchesTag = (ev.tags || []).some((t) => t.toLowerCase().includes(q));
        if (!matchesTitle && !matchesRad && !matchesJuzgado && !matchesProc && !matchesClient && !matchesTag) {
          return false;
        }
      }
      return true;
    });
  }, [enrichedEvents, selectedProcessFilter, selectedTypeFilter, selectedTagFilter, searchQuery]);

  // Próximo hito procesal destacado (audiencia o término más cercano)
  const nextCriticalEvent = useMemo(() => {
    const upcoming = enrichedEvents
      .filter((ev) => ev.diffDays >= 0 && ev.status !== "completed")
      .sort((a, b) => a.diffDays - b.diffDays);
    return upcoming[0] || null;
  }, [enrichedEvents]);

  // Métricas procesales
  const metrics = useMemo(() => {
    const audienciasCount = enrichedEvents.filter((e) => e.meta.type === "audiencia" && e.diffDays >= 0).length;
    const urgentCount = enrichedEvents.filter((e) => e.isUrgent && e.status !== "completed").length;
    const completedCount = enrichedEvents.filter((e) => e.status === "completed").length;
    const totalCount = enrichedEvents.length;

    return { audienciasCount, urgentCount, completedCount, totalCount };
  }, [enrichedEvents]);

  // Abrir modal de nuevo evento
  const handleOpenNewDialog = (presetDate?: string) => {
    setEditingDeadline(null);
    setEventForm({
      title: "",
      dueDate: presetDate || new Date().toISOString().split("T")[0],
      caseId: "general",
      type: "termino",
      time: "09:00",
      meetingUrl: "",
      priority: "alta",
      notes: ""
    });
    setIsDialogOpen(true);
  };

  // Abrir modal de edición
  const handleOpenEditDialog = (ev: typeof enrichedEvents[0]) => {
    setEditingDeadline(ev);
    setEventForm({
      title: ev.title,
      dueDate: ev.due_date,
      caseId: ev.case_id || "general",
      type: ev.meta.type,
      time: ev.meta.time || "09:00",
      meetingUrl: ev.meta.meetingUrl || "",
      priority: ev.meta.priority || "media",
      notes: ev.meta.rawNotes || ""
    });
    setIsDialogOpen(true);
  };

  // Guardar o Actualizar evento
  const handleSaveEvent = async () => {
    if (!eventForm.title.trim()) {
      toast({ title: "Título requerido", description: "Ingrese el nombre de la actuación o término judicial.", variant: "destructive" });
      return;
    }
    if (!eventForm.dueDate) {
      toast({ title: "Fecha requerida", description: "Seleccione la fecha de vencimiento o audiencia.", variant: "destructive" });
      return;
    }

    const effectiveCaseId = eventForm.caseId && eventForm.caseId !== "general" ? eventForm.caseId : null;
    const selectedProc = processes.find((p) => p.id === effectiveCaseId);

    const structuredMeta: StructuredEventMetadata = {
      type: eventForm.type,
      time: eventForm.time,
      meetingUrl: eventForm.meetingUrl,
      priority: eventForm.priority,
      juzgado: selectedProc?.juzgado || selectedProc?.despacho,
      radicado: selectedProc?.case_number || selectedProc?.radicado,
      clientName: selectedProc?.client_name,
      rawNotes: eventForm.notes
    };

    const descriptionPayload = JSON.stringify(structuredMeta);

    if (editingDeadline) {
      await updateEvent(editingDeadline.id, {
        title: eventForm.title,
        due_date: eventForm.dueDate,
        case_id: effectiveCaseId,
        description: descriptionPayload
      });
      sonnerToast.success("Actuación judicial actualizada");
    } else {
      await addEvent({
        title: eventForm.title,
        date: eventForm.dueDate,
        due_date: eventForm.dueDate,
        case_id: effectiveCaseId,
        description: descriptionPayload,
        status: "pending"
      });
      sonnerToast.success("Actuación judicial agendada");
    }

    setIsDialogOpen(false);
  };

  // Marcar como cumplido / evacuado
  const handleToggleComplete = async (id: string, currentStatus: string) => {
    const newStatus = currentStatus === "completed" ? "pending" : "completed";
    await updateEvent(id, { status: newStatus });
    if (newStatus === "completed") {
      sonnerToast.success("Término marcado como evacuado / cumplido");
    } else {
      sonnerToast.info("Término reabierto como pendiente");
    }
  };

  // Eliminar evento
  const handleDeleteEvent = async (id: string) => {
    if (window.confirm("¿Está seguro de eliminar esta fecha del calendario procesal?")) {
      await deleteEvent(id);
      sonnerToast.success("Hito eliminado del calendario");
    }
  };

  // Exportar a Google Calendar o generar .ICS
  const handleExportIcs = (ev: typeof enrichedEvents[0]) => {
    const title = encodeURIComponent(`[Rama Judicial] ${ev.title} - Rad. ${ev.radicado || "S.N."}`);
    const details = encodeURIComponent(
      `Despacho: ${ev.juzgado || "No especificado"}\nRadicado: ${ev.radicado || "S.N."}\nCliente: ${ev.clientName || "N/A"}\nSala Virtual: ${ev.meta.meetingUrl || "N/A"}\nNotas: ${ev.meta.rawNotes || ""}`
    );
    const location = encodeURIComponent(ev.meta.meetingUrl || ev.juzgado || "Juzgado de Conocimiento");
    
    // Formato Google Calendar URL
    const dateFormatted = ev.due_date.replace(/-/g, "");
    const timeFormatted = (ev.meta.time || "09:00").replace(":", "") + "00";
    const gCalUrl = `https://calendar.google.com/calendar/render?action=TEMPLATE&text=${title}&details=${details}&location=${location}&dates=${dateFormatted}T${timeFormatted}/${dateFormatted}T${timeFormatted}`;

    window.open(gCalUrl, "_blank");
    sonnerToast.success("Cita abierta en Google Calendar");
  };

  // Compartir citación de audiencia o término con el cliente por WhatsApp
  const handleShareWhatsApp = (ev: typeof enrichedEvents[0]) => {
    const text = encodeURIComponent(
      `*NOTIFICACIÓN JUDICIAL - DESPACHOS*\n\n` +
      `Estimado(a) ${ev.clientName || "Cliente"}:\n` +
      `Le informamos sobre la siguiente actuación procesal programada dentro de su expediente:\n\n` +
      `📌 *Hito:* ${ev.title}\n` +
      `⚖️ *Despacho:* ${ev.juzgado || "Juzgado de conocimiento"}\n` +
      `📄 *Radicado:* ${ev.radicado || "En trámite"}\n` +
      `📅 *Fecha:* ${ev.due_date}\n` +
      `⏰ *Hora:* ${ev.meta.time || "Por fijar"}\n` +
      (ev.meta.meetingUrl ? `🔗 *Sala Virtual (Teams):* ${ev.meta.meetingUrl}\n\n` : "\n") +
      `Atentamente,\nEquipo Jurídico MiA LegalTech.`
    );

    const waUrl = `https://wa.me/?text=${text}`;
    window.open(waUrl, "_blank");
  };

  // Aplicar fecha desde la calculadora
  const handleApplyCalculatedDate = () => {
    const calculated = calculateBusinessTermDate(calcForm.notifyDate, calcForm.termDays);
    const isoDate = calculated.toISOString().split("T")[0];
    setEventForm((prev) => ({
      ...prev,
      dueDate: isoDate,
      notes: `${prev.notes ? prev.notes + "\n" : ""}Término calculado: ${calcForm.termDays} días hábiles conforme al Art. 118 CGP a partir del ${calcForm.notifyDate}.`
    }));
    setIsCalcOpen(false);
    sonnerToast.success(`Fecha fijada al ${isoDate} (${calcForm.termDays} días hábiles)`);
  };

  // Navegación de mes
  const nextMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1));
  };
  const prevMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1));
  };
  const goToToday = () => {
    setCurrentDate(new Date());
  };

  // Días del mes para la vista mensual
  const calendarDays = useMemo(() => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();

    const firstDayOfMonth = new Date(year, month, 1);
    const lastDayOfMonth = new Date(year, month + 1, 0);

    const daysInMonth = lastDayOfMonth.getDate();
    const startingDayOfWeek = firstDayOfMonth.getDay(); // 0 = Domingo

    const days = [];

    // Días de relleno del mes anterior
    const prevMonthLastDay = new Date(year, month, 0).getDate();
    for (let i = startingDayOfWeek - 1; i >= 0; i--) {
      const d = new Date(year, month - 1, prevMonthLastDay - i);
      days.push({ date: d, isCurrentMonth: false });
    }

    // Días del mes actual
    for (let i = 1; i <= daysInMonth; i++) {
      const d = new Date(year, month, i);
      days.push({ date: d, isCurrentMonth: true });
    }

    // Días de relleno del mes siguiente para completar grilla de 35 o 42
    const remaining = (7 - (days.length % 7)) % 7;
    for (let i = 1; i <= remaining; i++) {
      const d = new Date(year, month + 1, i);
      days.push({ date: d, isCurrentMonth: false });
    }

    return days;
  }, [currentDate]);

  return (
    <div className="space-y-5 animate-fade-in text-slate-800">
      {/* ── 1. KPI CARDS SUPERIORES & BANNER DE PRÓXIMO HITO ── */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3.5">
        <div className="p-4 rounded-xl border border-slate-200 bg-white shadow-xs flex items-center justify-between">
          <div>
            <div className="text-xs text-slate-500 font-medium">Audiencias Fijadas</div>
            <div className="text-2xl font-bold text-slate-900 mt-0.5">{metrics.audienciasCount}</div>
            <div className="text-[11px] text-slate-400">Salas virtuales y presenciales</div>
          </div>
          <div className="h-10 w-10 rounded-xl bg-slate-100 flex items-center justify-center text-slate-700">
            <Video className="h-5 w-5" />
          </div>
        </div>

        <div className="p-4 rounded-xl border border-slate-200 bg-white shadow-xs flex items-center justify-between">
          <div>
            <div className="text-xs text-slate-500 font-medium">Términos en Riesgo</div>
            <div className="text-2xl font-bold text-amber-600 mt-0.5">{metrics.urgentCount}</div>
            <div className="text-[11px] text-amber-700 font-medium">&le; 3 días para vencer</div>
          </div>
          <div className="h-10 w-10 rounded-xl bg-amber-50 text-amber-700 border border-amber-200 flex items-center justify-center">
            <AlertTriangle className="h-5 w-5" />
          </div>
        </div>

        <div className="p-4 rounded-xl border border-slate-200 bg-white shadow-xs flex items-center justify-between">
          <div>
            <div className="text-xs text-slate-500 font-medium">Términos Evacuados</div>
            <div className="text-2xl font-bold text-emerald-700 mt-0.5">{metrics.completedCount}</div>
            <div className="text-[11px] text-emerald-600 font-medium">Cumplidos en tiempo</div>
          </div>
          <div className="h-10 w-10 rounded-xl bg-emerald-50 text-emerald-700 border border-emerald-200 flex items-center justify-center">
            <CheckCircle2 className="h-5 w-5" />
          </div>
        </div>

        <div className="p-4 rounded-xl border border-slate-200 bg-white shadow-xs flex items-center justify-between">
          <div>
            <div className="text-xs text-slate-500 font-medium">Total en Agenda</div>
            <div className="text-2xl font-bold text-slate-900 mt-0.5">{metrics.totalCount}</div>
            <div className="text-[11px] text-slate-400">{processes.length} procesos activos</div>
          </div>
          <div className="h-10 w-10 rounded-xl bg-slate-100 flex items-center justify-center text-slate-700">
            <CalendarCheck2 className="h-5 w-5" />
          </div>
        </div>
      </div>

      {/* ── BANNER PRÓXIMA AUDIENCIA / HITO CRÍTICO ── */}
      {nextCriticalEvent && (
        <div className="p-4 rounded-xl border border-slate-200 bg-slate-50/70 flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div className="flex items-start gap-3">
            <div className="h-9 w-9 rounded-xl bg-white border border-slate-200 flex items-center justify-center text-slate-800 shadow-xs shrink-0 mt-0.5">
              {nextCriticalEvent.meta.type === "audiencia" ? (
                <Video className="h-5 w-5 text-indigo-600" />
              ) : (
                <Gavel className="h-5 w-5 text-amber-600" />
              )}
            </div>
            <div>
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-xs font-bold uppercase tracking-wider text-slate-500">
                  Próxima Actuación Crítica:
                </span>
                <Badge
                  variant="outline"
                  className={cn(
                    "text-[10px] font-semibold py-0.5",
                    nextCriticalEvent.isToday
                      ? "bg-rose-50 text-rose-700 border-rose-200 animate-pulse"
                      : nextCriticalEvent.diffDays <= 3
                      ? "bg-amber-50 text-amber-800 border-amber-200"
                      : "bg-slate-100 text-slate-700 border-slate-200"
                  )}
                >
                  {nextCriticalEvent.isToday
                    ? "¡VENCE HOY!"
                    : nextCriticalEvent.diffDays === 1
                    ? "MAÑANA"
                    : `EN ${nextCriticalEvent.diffDays} DÍAS`}
                </Badge>
                {nextCriticalEvent.meta.time && (
                  <span className="text-xs text-slate-600 font-mono flex items-center gap-1">
                    <Clock className="h-3 w-3 text-slate-400" />
                    {nextCriticalEvent.meta.time}
                  </span>
                )}
              </div>
              <h4 className="text-sm font-bold text-slate-900 mt-1">
                {nextCriticalEvent.title}
              </h4>
              <p className="text-xs text-slate-600 mt-0.5">
                {nextCriticalEvent.juzgado && <span>{nextCriticalEvent.juzgado} &bull; </span>}
                {nextCriticalEvent.radicado && <span className="font-mono text-slate-700">Rad. {nextCriticalEvent.radicado}</span>}
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2 shrink-0">
            {nextCriticalEvent.meta.meetingUrl && (
              <Button
                size="sm"
                className="bg-indigo-700 hover:bg-indigo-600 text-white font-medium text-xs gap-1.5 shadow-xs"
                asChild
              >
                <a href={nextCriticalEvent.meta.meetingUrl} target="_blank" rel="noreferrer">
                  <Video className="h-3.5 w-3.5" />
                  Entrar a Sala Virtual
                </a>
              </Button>
            )}

            {nextCriticalEvent.case_id && (
              <Button
                size="sm"
                variant="outline"
                className="border-slate-300 bg-white hover:bg-slate-50 text-slate-700 text-xs gap-1.5 shadow-xs"
                asChild
              >
                <Link to={`/dashboard/despachos/procesos/${nextCriticalEvent.case_id}`}>
                  <Folder className="h-3.5 w-3.5" />
                  Ver Expediente
                </Link>
              </Button>
            )}

            <Button
              size="sm"
              variant="outline"
              className="border-slate-300 bg-white hover:bg-slate-50 text-slate-700 text-xs gap-1.5 shadow-xs"
              onClick={() => handleShareWhatsApp(nextCriticalEvent)}
              title="Notificar citación al cliente por WhatsApp"
            >
              <Share2 className="h-3.5 w-3.5 text-emerald-600" />
              Notificar Cliente
            </Button>
          </div>
        </div>
      )}

      {/* ── 2. BARRA DE HERRAMIENTAS, FILTROS Y CONTROLES ── */}
      <div className="p-4 rounded-xl border border-slate-200 bg-white shadow-xs flex flex-col md:flex-row items-stretch md:items-center justify-between gap-3">
        {/* Controles de Navegación Temporal */}
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={prevMonth}
            className="border-slate-300 text-slate-600 hover:bg-slate-50 h-8 w-8 p-0"
          >
            <ChevronLeft className="h-4 w-4" />
          </Button>

          <Button
            variant="outline"
            size="sm"
            onClick={goToToday}
            className="border-slate-300 text-slate-700 hover:bg-slate-50 text-xs h-8 font-medium"
          >
            Hoy
          </Button>

          <Button
            variant="outline"
            size="sm"
            onClick={nextMonth}
            className="border-slate-300 text-slate-600 hover:bg-slate-50 h-8 w-8 p-0"
          >
            <ChevronRight className="h-4 w-4" />
          </Button>

          <span className="text-sm font-bold text-slate-900 capitalize ml-2">
            {currentDate.toLocaleDateString("es-CO", { month: "long", year: "numeric" })}
          </span>
        </div>

        {/* Selector de Vistas y Buscador */}
        <div className="flex flex-wrap items-center gap-2">
          <div className="relative w-48 sm:w-56">
            <Search className="h-3.5 w-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input
              placeholder="Buscar radicado, juzgado..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-8 h-8 text-xs border-slate-300 bg-slate-50/50"
            />
          </div>

          <Select value={selectedTypeFilter} onValueChange={setSelectedTypeFilter}>
            <SelectTrigger className="w-36 h-8 text-xs border-slate-300 bg-white">
              <SelectValue placeholder="Tipo" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="todos">Todos los Tipos</SelectItem>
              <SelectItem value="audiencia">Audiencias</SelectItem>
              <SelectItem value="termino">Términos Procesales</SelectItem>
              <SelectItem value="estado">Notificaciones por Estado</SelectItem>
              <SelectItem value="diligencia">Diligencias</SelectItem>
            </SelectContent>
          </Select>

          {availableTags.length > 0 && (
            <Select value={selectedTagFilter} onValueChange={setSelectedTagFilter}>
              <SelectTrigger className="w-36 h-8 text-xs border-slate-300 bg-white">
                <Tag className="h-3 w-3 mr-1 text-slate-400" />
                <SelectValue placeholder="Etiqueta" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="todos">Todas las Etiquetas</SelectItem>
                {availableTags.map((tag) => (
                  <SelectItem key={tag} value={tag}>
                    {tag}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}

          <div className="flex border border-slate-200 bg-slate-100 p-0.5 rounded-lg">
            <button
              onClick={() => setViewMode("mes")}
              className={cn(
                "px-2.5 py-1 text-xs font-medium rounded-md transition-all",
                viewMode === "mes" ? "bg-white text-slate-900 shadow-xs font-semibold" : "text-slate-600 hover:text-slate-900"
              )}
            >
              Mes
            </button>
            <button
              onClick={() => setViewMode("vencimientos")}
              className={cn(
                "px-2.5 py-1 text-xs font-medium rounded-md transition-all",
                viewMode === "vencimientos" ? "bg-white text-slate-900 shadow-xs font-semibold" : "text-slate-600 hover:text-slate-900"
              )}
            >
              Vencimientos
            </button>
          </div>

          <Button
            size="sm"
            variant="outline"
            className="border-slate-300 bg-white hover:bg-slate-50 text-slate-700 text-xs gap-1.5 h-8 font-medium shadow-xs"
            onClick={() => setIsCalcOpen(true)}
          >
            <Calculator className="h-3.5 w-3.5 text-slate-500" />
            Calculadora CGP
          </Button>

          <Button
            size="sm"
            className="bg-slate-900 hover:bg-slate-800 text-white font-medium text-xs gap-1.5 h-8 shadow-xs"
            onClick={() => handleOpenNewDialog()}
          >
            <Plus className="h-3.5 w-3.5" />
            Agendar Actuación
          </Button>
        </div>
      </div>

      {/* ── 3. VISTA MES (GRILLA COMPLETA CON FESTIVOS DE COLOMBIA) ── */}
      {viewMode === "mes" && (
        <Card className="border border-slate-200 bg-white shadow-xs overflow-hidden">
          {/* Cabecera de días de la semana */}
          <div className="grid grid-cols-7 border-b border-slate-200 bg-slate-50 text-center text-xs font-semibold text-slate-600">
            {["Domingo", "Lunes", "Martes", "Miércoles", "Jueves", "Viernes", "Sábado"].map((day, idx) => (
              <div key={day} className={cn("py-2.5 border-r last:border-r-0 border-slate-200", (idx === 0 || idx === 6) && "text-slate-400 bg-slate-100/50")}>
                <span className="hidden sm:inline">{day}</span>
                <span className="sm:hidden">{day.slice(0, 3)}</span>
              </div>
            ))}
          </div>

          {/* Grilla de Días */}
          <div className="grid grid-cols-7 divide-x divide-y divide-slate-200">
            {calendarDays.map(({ date, isCurrentMonth }, idx) => {
              const iso = date.toISOString().split("T")[0];
              const holidayInfo = isNonWorkingDay(date);
              const isToday = iso === new Date().toISOString().split("T")[0];

              // Eventos en este día
              const dayEvents = filteredEvents.filter((ev) => ev.due_date === iso);

              return (
                <div
                  key={idx}
                  onClick={() => handleOpenNewDialog(iso)}
                  className={cn(
                    "min-h-[110px] p-2 transition-colors relative flex flex-col justify-between cursor-pointer group",
                    !isCurrentMonth ? "bg-slate-50/50 opacity-40" : "bg-white hover:bg-slate-50/70",
                    holidayInfo.isNonWorking && isCurrentMonth && "bg-slate-50/30",
                    isToday && "bg-blue-50/20"
                  )}
                >
                  {/* Encabezado del día */}
                  <div className="flex items-center justify-between mb-1">
                    <span
                      className={cn(
                        "h-6 w-6 flex items-center justify-center rounded-full text-xs font-bold",
                        isToday ? "bg-slate-900 text-white" : "text-slate-700"
                      )}
                    >
                      {date.getDate()}
                    </span>

                    {holidayInfo.isNonWorking && holidayInfo.reason && holidayInfo.reason !== "Sábado" && holidayInfo.reason !== "Domingo" && (
                      <span className="text-[9px] text-amber-700 bg-amber-50 px-1.5 py-0.5 rounded border border-amber-200 font-medium truncate max-w-[90px]" title={holidayInfo.reason}>
                        {holidayInfo.reason}
                      </span>
                    )}
                  </div>

                  {/* Lista de eventos del día */}
                  <div className="space-y-1 flex-1 overflow-hidden">
                    {dayEvents.slice(0, 3).map((ev) => {
                      const isCompleted = ev.status === "completed";
                      return (
                        <div
                          key={ev.id}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleOpenEditDialog(ev);
                          }}
                          className={cn(
                            "text-[10px] p-1.5 rounded-lg border font-medium truncate flex items-center justify-between gap-1 transition-transform hover:scale-[1.02]",
                            isCompleted
                              ? "bg-slate-100 text-slate-400 border-slate-200 line-through"
                              : ev.meta.type === "audiencia"
                              ? "bg-indigo-50/80 text-indigo-900 border-indigo-200/80"
                              : ev.isUrgent
                              ? "bg-amber-50/90 text-amber-900 border-amber-200"
                              : "bg-slate-50 text-slate-800 border-slate-200"
                          )}
                          title={`${ev.title} ${ev.radicado ? `(Rad: ${ev.radicado})` : ""}`}
                        >
                          <span className="truncate">
                            {ev.meta.type === "audiencia" ? "⚖️ " : "⏳ "}
                            {ev.title}
                          </span>
                          {ev.meta.time && (
                            <span className="text-[9px] font-mono text-slate-500 shrink-0">
                              {ev.meta.time}
                            </span>
                          )}
                        </div>
                      );
                    })}

                    {dayEvents.length > 3 && (
                      <div className="text-[9px] text-slate-500 font-semibold pl-1">
                        +{dayEvents.length - 3} actuaciones más
                      </div>
                    )}
                  </div>

                  {/* Botón flotante para agregar rápido */}
                  <div className="opacity-0 group-hover:opacity-100 transition-opacity flex justify-end pt-1">
                    <span className="text-[10px] text-slate-400 flex items-center gap-0.5">
                      <Plus className="h-3 w-3" /> Agendar
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        </Card>
      )}

      {/* ── 4. VISTA DE VENCIMIENTOS (LISTA CRONOLÓGICA & URGENCIA) ── */}
      {viewMode === "vencimientos" && (
        <div className="space-y-3">
          {filteredEvents.length === 0 ? (
            <Card className="p-12 text-center border-slate-200 bg-white">
              <Clock className="h-10 w-10 text-slate-300 mx-auto mb-3" />
              <h3 className="text-base font-bold text-slate-800">No hay actuaciones judiciales agendadas</h3>
              <p className="text-xs text-slate-500 mt-1 max-w-sm mx-auto">
                No se encontraron términos ni audiencias con los filtros aplicados.
              </p>
              <Button size="sm" onClick={() => handleOpenNewDialog()} className="mt-4 bg-slate-900 text-white text-xs">
                <Plus className="h-3.5 w-3.5 mr-1" /> Agendar Primer Término
              </Button>
            </Card>
          ) : (
            filteredEvents
              .sort((a, b) => new Date(a.due_date).getTime() - new Date(b.due_date).getTime())
              .map((ev) => {
                const isCompleted = ev.status === "completed";
                return (
                  <Card
                    key={ev.id}
                    className={cn(
                      "border bg-white shadow-xs hover:shadow-md transition-shadow overflow-hidden",
                      isCompleted ? "opacity-60 border-slate-200 bg-slate-50/40" : ev.isUrgent ? "border-amber-300/80" : "border-slate-200"
                    )}
                  >
                    <div className="p-4 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                      {/* Fecha e Indicador */}
                      <div className="flex items-start gap-3.5">
                        <div
                          className={cn(
                            "w-14 py-2 rounded-xl flex flex-col items-center justify-center border shrink-0 text-center",
                            ev.isToday
                              ? "bg-rose-50 border-rose-200 text-rose-800"
                              : ev.isUrgent
                              ? "bg-amber-50 border-amber-200 text-amber-800"
                              : "bg-slate-50 border-slate-200 text-slate-800"
                          )}
                        >
                          <span className="text-[10px] uppercase font-bold text-slate-500">
                            {new Date(ev.due_date + "T00:00:00").toLocaleDateString("es-CO", { weekday: "short" })}
                          </span>
                          <span className="text-lg font-black leading-tight">
                            {new Date(ev.due_date + "T00:00:00").getDate()}
                          </span>
                          <span className="text-[9px] uppercase text-slate-500">
                            {new Date(ev.due_date + "T00:00:00").toLocaleDateString("es-CO", { month: "short" })}
                          </span>
                        </div>

                        {/* Detalles de la actuación */}
                        <div className="space-y-1">
                          <div className="flex items-center gap-2 flex-wrap">
                            <Badge
                              variant="outline"
                              className={cn(
                                "text-[10px] font-semibold py-0.5",
                                ev.meta.type === "audiencia"
                                  ? "bg-indigo-50 text-indigo-800 border-indigo-200"
                                  : ev.meta.type === "termino"
                                  ? "bg-amber-50 text-amber-800 border-amber-200"
                                  : "bg-slate-100 text-slate-700 border-slate-200"
                              )}
                            >
                              {ev.meta.type === "audiencia"
                                ? "Audiencia Judicial"
                                : ev.meta.type === "termino"
                                ? "Término Procesal"
                                : ev.meta.type === "estado"
                                ? "Notificación Estado"
                                : "Diligencia"}
                            </Badge>

                            {ev.isToday && (
                              <Badge className="bg-rose-600 text-white text-[10px] animate-pulse">
                                Vence Hoy
                              </Badge>
                            )}

                            {ev.meta.time && (
                              <span className="text-xs text-slate-500 font-mono flex items-center gap-1">
                                <Clock className="h-3 w-3 text-slate-400" />
                                {ev.meta.time}
                              </span>
                            )}
                          </div>

                          <h4 className={cn("text-sm font-bold text-slate-900", isCompleted && "line-through text-slate-400")}>
                            {ev.title}
                          </h4>

                          <div className="flex items-center gap-3 text-xs text-slate-500 flex-wrap">
                            {ev.juzgado && (
                              <span className="flex items-center gap-1">
                                <Building className="h-3 w-3 text-slate-400" />
                                {ev.juzgado}
                              </span>
                            )}

                            {ev.radicado && (
                              <span className="font-mono text-slate-700 bg-slate-100 px-1.5 py-0.5 rounded text-[11px]">
                                Rad. {ev.radicado}
                              </span>
                            )}

                            {ev.clientName && (
                              <span className="flex items-center gap-1 text-slate-600">
                                <User className="h-3 w-3 text-slate-400" />
                                {ev.clientName}
                              </span>
                            )}

                            {/* Etiquetas del proceso */}
                            {ev.tags && ev.tags.length > 0 && (
                              <div className="flex items-center gap-1">
                                {ev.tags.slice(0, 3).map((t: string) => (
                                  <Badge
                                    key={t}
                                    variant="outline"
                                    className="text-[9px] py-0 px-1.5 h-4 bg-slate-100/90 text-slate-700 border-slate-200 font-medium"
                                  >
                                    <Tag className="h-2 w-2 mr-0.5 opacity-50" />
                                    {t}
                                  </Badge>
                                ))}
                                {ev.tags.length > 3 && (
                                  <span className="text-[9px] text-slate-400 font-semibold">
                                    +{ev.tags.length - 3}
                                  </span>
                                )}
                              </div>
                            )}
                          </div>

                          {ev.meta.rawNotes && (
                            <p className="text-xs text-slate-500 italic mt-1 line-clamp-1">
                              "{ev.meta.rawNotes}"
                            </p>
                          )}
                        </div>
                      </div>

                      {/* Botones de acción procesal */}
                      <div className="flex items-center gap-2 shrink-0">
                        {ev.meta.meetingUrl && (
                          <Button
                            size="sm"
                            variant="outline"
                            className="border-indigo-200 bg-indigo-50/60 hover:bg-indigo-100 text-indigo-900 text-xs gap-1.5 h-8 font-medium shadow-xs"
                            asChild
                          >
                            <a href={ev.meta.meetingUrl} target="_blank" rel="noreferrer">
                              <Video className="h-3.5 w-3.5 text-indigo-700" />
                              Sala Teams
                            </a>
                          </Button>
                        )}

                        {ev.case_id && (
                          <Button
                            size="sm"
                            variant="outline"
                            className="border-slate-300 bg-white hover:bg-slate-50 text-slate-700 text-xs gap-1.5 h-8 font-medium shadow-xs"
                            asChild
                          >
                            <Link to={`/dashboard/despachos/procesos/${ev.case_id}`}>
                              <Folder className="h-3.5 w-3.5 text-slate-500" />
                              Expediente
                            </Link>
                          </Button>
                        )}

                        <Button
                          size="sm"
                          variant="outline"
                          className="border-slate-300 bg-white hover:bg-slate-50 text-slate-700 text-xs h-8 px-2"
                          onClick={() => handleExportIcs(ev)}
                          title="Sincronizar con Google Calendar"
                        >
                          <CalendarIcon className="h-3.5 w-3.5 text-slate-500" />
                        </Button>

                        <Button
                          size="sm"
                          variant="outline"
                          className="border-slate-300 bg-white hover:bg-slate-50 text-slate-700 text-xs h-8 px-2"
                          onClick={() => handleShareWhatsApp(ev)}
                          title="Compartir citación por WhatsApp"
                        >
                          <Share2 className="h-3.5 w-3.5 text-emerald-600" />
                        </Button>

                        <Button
                          size="sm"
                          variant={isCompleted ? "secondary" : "outline"}
                          className={cn(
                            "text-xs h-8 font-medium shadow-xs",
                            isCompleted ? "bg-emerald-100 text-emerald-800 hover:bg-emerald-200" : "border-slate-300 text-slate-700"
                          )}
                          onClick={() => handleToggleComplete(ev.id, ev.status)}
                        >
                          <Check className="h-3.5 w-3.5 mr-1" />
                          {isCompleted ? "Evacuado" : "Cumplir"}
                        </Button>

                        <Button
                          size="sm"
                          variant="ghost"
                          className="h-8 w-8 p-0 text-slate-400 hover:text-slate-700"
                          onClick={() => handleOpenEditDialog(ev)}
                          title="Editar"
                        >
                          <Edit2 className="h-3.5 w-3.5" />
                        </Button>

                        <Button
                          size="sm"
                          variant="ghost"
                          className="h-8 w-8 p-0 text-slate-400 hover:text-rose-600"
                          onClick={() => handleDeleteEvent(ev.id)}
                          title="Eliminar"
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      </div>
                    </div>
                  </Card>
                );
              })
          )}
        </div>
      )}

      {/* ── 5. DIÁLOGO: AGENDAR / EDITAR ACTUACIÓN JUDICIAL ── */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="max-w-xl rounded-2xl bg-white text-slate-800 border-slate-200">
          <DialogHeader>
            <DialogTitle className="text-lg font-bold text-slate-900 flex items-center gap-2">
              <Gavel className="h-5 w-5 text-slate-700" />
              {editingDeadline ? "Editar Hito Procesal" : "Agendar Actuación o Término Judicial"}
            </DialogTitle>
            <DialogDescription className="text-xs text-slate-500">
              Vincule la fecha a un proceso monitoreado en Despachos para mantener el control procesal.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-2 text-xs">
            {/* Vinculación con Proceso */}
            <div className="space-y-1.5">
              <label className="font-semibold text-slate-700">Expediente / Proceso de Despachos:</label>
              <Select
                value={eventForm.caseId || "general"}
                onValueChange={(val) => setEventForm((prev) => ({ ...prev, caseId: val }))}
              >
                <SelectTrigger className="border-slate-300 bg-white text-xs h-9">
                  <SelectValue placeholder="Seleccione un proceso (opcional)" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="general">Ninguno (Evento General de Despacho)</SelectItem>
                  {processes.map((proc) => (
                    <SelectItem key={proc.id} value={proc.id}>
                      {proc.case_number ? `Rad. ${proc.case_number} - ` : ""}
                      {proc.title}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Tipo de Evento y Prioridad */}
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <label className="font-semibold text-slate-700">Tipo de Actuación:</label>
                <Select
                  value={eventForm.type}
                  onValueChange={(val: EventType) => setEventForm((prev) => ({ ...prev, type: val }))}
                >
                  <SelectTrigger className="border-slate-300 bg-white text-xs h-9">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="audiencia">⚖️ Audiencia Judicial</SelectItem>
                    <SelectItem value="termino">⏳ Vencimiento de Término</SelectItem>
                    <SelectItem value="estado">📜 Notificación por Estado</SelectItem>
                    <SelectItem value="diligencia">💼 Diligencia Judicial</SelectItem>
                    <SelectItem value="otro">📌 Otro Hito</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-1.5">
                <label className="font-semibold text-slate-700">Nivel de Prioridad:</label>
                <Select
                  value={eventForm.priority}
                  onValueChange={(val: "alta" | "media" | "baja") => setEventForm((prev) => ({ ...prev, priority: val }))}
                >
                  <SelectTrigger className="border-slate-300 bg-white text-xs h-9">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="alta">🔴 Alta (Riesgo de Preclusión)</SelectItem>
                    <SelectItem value="media">🟡 Media (Gestión Ordinaria)</SelectItem>
                    <SelectItem value="baja">🟢 Baja (Informativo)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Título de la Actuación */}
            <div className="space-y-1.5">
              <label className="font-semibold text-slate-700">Título de la Actuación o Audiencia:</label>
              <Input
                placeholder="Ej. Audiencia Inicial Art. 372 CGP / Vencimiento término para apelar auto"
                value={eventForm.title}
                onChange={(e) => setEventForm((prev) => ({ ...prev, title: e.target.value }))}
                className="border-slate-300 text-xs h-9"
              />
            </div>

            {/* Fecha y Hora + Botón Calculadora */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <div className="flex items-center justify-between">
                  <label className="font-semibold text-slate-700">Fecha de Vencimiento / Audiencia:</label>
                  <button
                    type="button"
                    onClick={() => setIsCalcOpen(true)}
                    className="text-[10px] text-indigo-600 hover:underline flex items-center gap-0.5"
                  >
                    <Calculator className="h-3 w-3" /> Calcular hábiles
                  </button>
                </div>
                <Input
                  type="date"
                  value={eventForm.dueDate}
                  onChange={(e) => setEventForm((prev) => ({ ...prev, dueDate: e.target.value }))}
                  className="border-slate-300 text-xs h-9 font-mono"
                />
              </div>

              <div className="space-y-1.5">
                <label className="font-semibold text-slate-700">Hora fijada por el Despacho:</label>
                <Input
                  type="time"
                  value={eventForm.time}
                  onChange={(e) => setEventForm((prev) => ({ ...prev, time: e.target.value }))}
                  className="border-slate-300 text-xs h-9 font-mono"
                />
              </div>
            </div>

            {/* Enlace de Sala Virtual */}
            {eventForm.type === "audiencia" && (
              <div className="space-y-1.5 animate-fadeIn">
                <label className="font-semibold text-slate-700 flex items-center gap-1.5">
                  <Video className="h-3.5 w-3.5 text-indigo-600" />
                  Enlace de Sala Virtual (Teams / Lifesize / Zoom del Juzgado):
                </label>
                <Input
                  placeholder="https://teams.microsoft.com/l/meetup-join/..."
                  value={eventForm.meetingUrl}
                  onChange={(e) => setEventForm((prev) => ({ ...prev, meetingUrl: e.target.value }))}
                  className="border-slate-300 text-xs h-9 font-mono"
                />
              </div>
            )}

            {/* Notas Procesales Adicionales */}
            <div className="space-y-1.5">
              <label className="font-semibold text-slate-700">Instrucciones o Notas Procesales:</label>
              <Textarea
                placeholder="Anotaciones sobre pruebas decretadas, objeciones o traslado de excepciones..."
                value={eventForm.notes}
                onChange={(e) => setEventForm((prev) => ({ ...prev, notes: e.target.value }))}
                className="border-slate-300 text-xs min-h-[70px]"
              />
            </div>
          </div>

          <DialogFooter className="gap-2 sm:gap-0">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setIsDialogOpen(false)}
              className="border-slate-300 text-slate-700 text-xs"
            >
              Cancelar
            </Button>
            <Button
              size="sm"
              onClick={handleSaveEvent}
              className="bg-slate-900 hover:bg-slate-800 text-white font-medium text-xs"
            >
              {editingDeadline ? "Guardar Cambios" : "Agendar Hito"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── 6. MODAL: CALCULADORA PROCESAL DE DÍAS HÁBILES (ART. 118 CGP) ── */}
      <Dialog open={isCalcOpen} onOpenChange={setIsCalcOpen}>
        <DialogContent className="max-w-md rounded-2xl bg-white text-slate-800 border-slate-200">
          <DialogHeader>
            <DialogTitle className="text-base font-bold text-slate-900 flex items-center gap-2">
              <Calculator className="h-4 w-4 text-indigo-600" />
              Calculadora de Términos Procesales (Art. 118 CGP)
            </DialogTitle>
            <DialogDescription className="text-xs text-slate-500">
              Descuenta automáticamente sábados, domingos, festivos oficiales de Colombia y vacancia judicial.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-3 text-xs">
            <div className="space-y-1.5">
              <label className="font-semibold text-slate-700">Fecha de Notificación / Fijación en Estado:</label>
              <Input
                type="date"
                value={calcForm.notifyDate}
                onChange={(e) => setCalcForm((prev) => ({ ...prev, notifyDate: e.target.value }))}
                className="border-slate-300 text-xs h-9 font-mono"
              />
              <p className="text-[11px] text-slate-400">
                El término comienza a correr a partir del primer día hábil siguiente a esta fecha.
              </p>
            </div>

            <div className="space-y-2">
              <label className="font-semibold text-slate-700">Presets Legales Frecuentes:</label>
              <div className="grid grid-cols-2 gap-2">
                {[
                  { days: 3, label: "3 Días (Reposición)" },
                  { days: 5, label: "5 Días (Subsanación)" },
                  { days: 10, label: "10 Días (Apelación / Alegatos)" },
                  { days: 20, label: "20 Días (Contestación Verbal)" },
                  { days: 30, label: "30 Días (Ejecutivo / Laboral)" }
                ].map((item) => (
                  <button
                    key={item.days}
                    type="button"
                    onClick={() => setCalcForm((prev) => ({ ...prev, termDays: item.days }))}
                    className={cn(
                      "p-2 rounded-lg border text-left transition-all text-xs",
                      calcForm.termDays === item.days
                        ? "bg-indigo-50 border-indigo-300 text-indigo-900 font-bold"
                        : "bg-slate-50 border-slate-200 text-slate-700 hover:bg-slate-100"
                    )}
                  >
                    {item.label}
                  </button>
                ))}
              </div>
            </div>

            <div className="space-y-1.5">
              <label className="font-semibold text-slate-700">O ingrese otro número de días hábiles:</label>
              <Input
                type="number"
                min="1"
                max="180"
                value={calcForm.termDays}
                onChange={(e) => setCalcForm((prev) => ({ ...prev, termDays: parseInt(e.target.value) || 1 }))}
                className="border-slate-300 text-xs h-9 font-mono"
              />
            </div>

            {/* Resultado calculado */}
            <div className="p-3.5 rounded-xl bg-slate-50 border border-slate-200 space-y-1">
              <div className="text-[10px] font-bold uppercase text-slate-500">Fecha Límite Calculada:</div>
              <div className="text-base font-bold text-slate-900 capitalize">
                {calculateBusinessTermDate(calcForm.notifyDate, calcForm.termDays).toLocaleDateString("es-CO", {
                  weekday: "long",
                  year: "numeric",
                  month: "long",
                  day: "numeric"
                })}
              </div>
              <div className="text-[11px] text-slate-500 font-mono">
                ISO: {calculateBusinessTermDate(calcForm.notifyDate, calcForm.termDays).toISOString().split("T")[0]}
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setIsCalcOpen(false)}
              className="border-slate-300 text-slate-700 text-xs"
            >
              Cerrar
            </Button>
            <Button
              size="sm"
              onClick={handleApplyCalculatedDate}
              className="bg-indigo-700 hover:bg-indigo-600 text-white font-medium text-xs gap-1.5"
            >
              <CalendarCheck2 className="h-3.5 w-3.5" />
              Aplicar al Evento
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

